import React, { useEffect, useMemo, useState } from "react"
import PropTypes from "prop-types"
import Input from "components/form/Input"
import PhotoSelector from "components/form/PhotoSelector"
import Loading from "modules/loader-watchers/Loading"
import Icon from "components/common/Icon"
import InputError from "components/form/InputError"
import { useTranslation } from "react-i18next"
import { serialize } from "object-to-formdata"
import { isEqual } from "lodash"
import useForm from "hooks/useForm"
import moment from "moment/moment"
import { useDispatch, useSelector } from "react-redux"
import { parseExpenseReceipt, saveExpense, addCategory } from "store/accounting"
import { metadataSelector } from "store/selectors"
import { Delete } from "ui/Icon/DSIcon/Delete"
import IconWithBg from "ui/IconWithBg"
import styles from "./Form.module.css"
import AddCategoryModal from "./AddCategoryModal"
import { Button, Col, Flex, Row, Select, DatePicker, Tag } from "antd"
import { Text } from "ui/Typography"
import { toaster } from "components/common/Toast"
import FirstLoading from "modules/loader-watchers/FirstLoading"
import { PlusIcon } from "ui/Icon/DSIcon/Plus"
import dayjs from "dayjs"

const recurringType = ["monthly", "yearly"]
const Form = ({ onChanged, expenseId, expense, setVisible }) => {
  const [isOpenAddCategoryModal, setIsOpenAddCategoryModal] = useState(false)
  const [isExpenseItemsChanged, setIsExpenseItemsChanged] = useState(false)
  const [categoryName, setCategoryName] = useState("")
  const [isUploadReceipt, setisUploadReceipt] = useState(false)
  // Keep receipts always as an array for simpler payload construction
  const [receiptImageUrl, setReceiptImageUrl] = useState([])
  const [expenseAmountError, setExpenseAmountError] = useState("")
  const [expenseAmountInfo, setExpenseAmountInfo] = useState("")
  const [totalExpenseItemAmount, setTotalExpenseItemAmount] = useState(0)
  const [isSaving, setIsSaving] = useState(false)
  const [isShowOverrideAmountCheckbox, setisShowOverrideAmountCheckbox] = useState(false)
  const [isChecked, setIsChecked] = useState(false)
  // Calculate total amount from expense items
  const { t } = useTranslation()
  const dispatch = useDispatch()

  useEffect(() => {
    if (expense?.expense_line_items && Array.isArray(expense?.expense_line_items)) {
      setExpenseItems(expense?.expense_line_items?.map((item) => ({ ...item })))
    }
  }, [expense?.expense_line_items])
  useEffect(() => {
    if (Array.isArray(expense?.receipts) && expense.receipts.length > 0) {
      setReceiptImageUrl(
        expense.receipts.map((receipt) => ({
          url: receipt?.url,
          id: receipt?.id
        }))
      )
    } else {
      setReceiptImageUrl([])
    }
  }, [expense?.receipts])
  const receiptParsing = useSelector((store) => store?.accounting.receiptParsing)
  const metadata = useSelector(metadataSelector("accounting"))
  const isExpensePersisted = !!expenseId
  const merchant_name = expense?.merchant_name
  // Initialize form with default purchase_date if missing, formatted as MM/DD/YYYY
  const initialPurchaseDate = expense?.purchase_date
    ? moment(expense.purchase_date, ["YYYY-MM-DD", "MM/DD/YYYY", moment.ISO_8601]).format("MM/DD/YYYY")
    : moment().format("MM/DD/YYYY")

  const [form, changeHandler, submitHandler, submitCallback, , isChanged] = useForm(
    {
      ...expense,
      expense_category_id: expense?.category?.id,
      photos: expenseId === undefined || expense?.receipts[0]?.placeholder_url === null ? [] : expense?.receipts,
      merchant_name,
      purchase_date: initialPurchaseDate
    },
    ["expense_category_id", "merchant_id", "merchant_name", "amount", "purchase_date", "recurring_type", "recurring", "notes", "photos"]
  )
  // Expense items state for dynamic rows
  const [expenseItems, setExpenseItems] = useState([{ name: "", amount: "", taxes_fees: "", expense_category_id: "" }])
  const filteredExpenseItems = expenseItems.filter((item) => !item._destroy)
  // Handle change for dynamic expense items
  const handleExpenseItemChange = (idx, field, value) => {
    setExpenseItems((prev) => {
      const updated = [...prev]
      updated[idx][field] = value
      changeHandler({ target: { name: "items", value: updated } })
      return updated
    })
    setIsExpenseItemsChanged(true)
  }

  // Add new expense item row
  const handleAddExpenseItem = () => {
    setExpenseItems((prev) => [...prev, { merchant_name: "", amount: "", expense_category_id: "" }])
  }

  // Remove expense item row
  const handleRemoveExpenseItem = (idx) => {
    setExpenseItems((prev) =>
      prev
        .map((item, i) => {
          if (i !== idx) return item
          if (item.id) return { ...item, _destroy: true }
          return null
        })
        .filter((item) => item !== null)
    )
    setIsExpenseItemsChanged(true)
  }
  const receipts = useMemo(() => form.photos?.filter(({ _destroy }) => !_destroy), [form.photos])
  // Remove receipt helper extracted from inline onClick for clarity & reuse
  const handleRemoveReceipt = () => {
    const destroyedPhotos = Array.isArray(form.photos) ? form.photos.map((p) => ({ ...p, _destroy: true })) : []
    changeHandler({ target: { name: "photos", value: destroyedPhotos } })
    setReceiptImageUrl((prev) => {
      if (Array.isArray(prev)) return prev.map((r) => ({ ...r, _destroy: true }))
      if (prev && typeof prev === "object") return [{ ...prev, _destroy: true }]
      return []
    })
    setisUploadReceipt(false)
  }
  useEffect(() => {
    const hasReceiptContext = isUploadReceipt || (expense?.receipts?.length > 0 && expense?.receipts?.[0]?.url != null)
    if (form.amount && expenseItems?.length) {
      const totalExpenseAmount = expenseItems.reduce((sum, item) => {
        if (!item._destroy && item.amount) {
          return sum + Number.parseFloat(item.amount)
        }
        return sum
      }, 0)
      setTotalExpenseItemAmount(totalExpenseAmount)
      const itemTotal = totalExpenseAmount.toFixed(2)
      const receiptAmount = Number.parseFloat(form.amount).toFixed(2)

      if (hasReceiptContext) {
        // Compare rounded values to avoid floating-point precision issues only when receipt context is present
        const itemTotalNum = Number.parseFloat(itemTotal)
        const receiptAmountNum = Number.parseFloat(receiptAmount)
        if (itemTotalNum > receiptAmountNum) {
          // Item total exceeds receipt amount -> error (block save)
          setisShowOverrideAmountCheckbox(true)
          setExpenseAmountError(t("expense.error_message", { itemTotal, receiptAmount }))
          setExpenseAmountInfo("")
        } else if (itemTotalNum < receiptAmountNum) {
          // Item total less than receipt -> warn but allow save
          setisShowOverrideAmountCheckbox(true)
          setExpenseAmountInfo(t("expense.warning_message", { itemTotal, receiptAmount }))
          setExpenseAmountError("")
        } else {
          setExpenseAmountError("")
          setExpenseAmountInfo("")
          setisShowOverrideAmountCheckbox(false)
        }
      } else {
        // No receipt context: clear messages
        setisShowOverrideAmountCheckbox(false)
        setExpenseAmountError("")
        setExpenseAmountInfo("")
      }
    } else {
      // Missing amount or items: clear messages
      setisShowOverrideAmountCheckbox(false)
      setExpenseAmountError("")
      setExpenseAmountInfo("")
    }
  }, [form.amount, expenseItems, isUploadReceipt, expense?.receipts?.length, t])
  submitCallback(() => {
    setIsSaving(true)
    // Build items array from expenseItems
    const items = expenseItems.map((item) =>
      item._destroy
        ? { id: item.id, _destroy: true }
        : {
            ...(item.id && { id: item.id }),
            name: item.name,
            amount: item.amount === "" ? 0 : Number(item.amount),
            expense_category_id: item.expense_category_id
          }
    )
    const total = expenseItems.filter((item) => !item._destroy).reduce((sum, item) => sum + Number.parseFloat(item.amount || 0), 0)
    const roundedTotal = Number.parseFloat(total.toFixed(2))
    // Choose amount: use form.amount if provided, else fallback to summed line items
    const effectiveAmount =
      expense?.receipts && expense.receipts.length > 0 && expense.receipts[0]?.url != null && !isChecked
        ? Number(Number.parseFloat(form.amount || 0).toFixed(2))
        : roundedTotal
    // Format purchase_date to YYYY-MM-DD for backend
    const formattedPurchaseDate = form.purchase_date
      ? moment(form.purchase_date, ["MM/DD/YYYY", moment.ISO_8601]).format("YYYY-MM-DD")
      : null
    // Build plain JSON payload
    const payload = {
      merchant_name: form.merchant_name,
      recurring: form.recurring || false,
      recurring_type: form.recurring ? form.recurring_type : "",
      amount: effectiveAmount,
      purchase_date: formattedPurchaseDate,
      currency: form.currency,
      notes: form.notes,
      expense_line_items_attributes: items,
      destroy_all: isUploadReceipt,
      receipts_attributes: receiptImageUrl // always an array
    }

    // Validation: recurring requires a valid recurring_type
    const isRecurring = form.recurring
    const validRecurringType = recurringType.includes(form.recurring_type)
    if (isRecurring && !validRecurringType) {
      toaster.error(t("expense.recurring_type_error"))
      setIsSaving(false)
      return
    }

    // Prevent save if required fields are empty
    if (
      !form.merchant_name ||
      !form.purchase_date ||
      !expenseItems.length ||
      expenseItems.some((item) => !item.name || !item.amount || !item.expense_category_id)
    ) {
      toaster.error("Please fill all required fields.")
      setIsSaving(false)
      return
    }
    // Prevent API trigger if error is present unless override checkbox is checked
    if (expenseAmountError && !isChecked) {
      setIsSaving(false)
      return
    }
    dispatch(saveExpense(expense?.id, payload))
      .then((res) => {
        setVisible(false)
        if (res?.message) {
          toaster.success(res.message)
        }
      })
      .finally(() => {
        setIsSaving(false)
      })
  })
  useEffect(() => {
    if (!receipts?.length || isEqual(expense?.receipts, receipts)) return
    const { file } = receipts[0]
    if (!file) return
    const formData = serialize({ file })
    dispatch(parseExpenseReceipt(formData))
      .then((res) => {
        const data = res?.data || res // support different shapes
        if (!data) throw new Error("Empty receipt parse response")
        setReceiptImageUrl([{ image: data?.image_url }])
        setisUploadReceipt(true)
        if (data.amount) changeHandler({ target: { name: "amount", value: data.amount } })
        if (data.purchase_date) {
          const formattedDate = moment(data.purchase_date, ["YYYY-MM-DD", moment.ISO_8601]).format("MM/DD/YYYY")
          changeHandler({ target: { name: "purchase_date", value: formattedDate } })
        }
        if (data.merchant_name) changeHandler({ target: { name: "merchant_name", value: data.merchant_name } })
        if (data.items) {
          if (Array.isArray(data.items) && data.items.length > 0) {
            setExpenseItems(
              data.items.map((item) => ({
                name: item.name || "",
                amount: item.amount || "",
                expense_category_id: "" // set default or map if available
              }))
            )
          } else {
            // Ensure at least one blank row if items array is empty
            setExpenseItems([{ name: "", amount: "", expense_category_id: "" }])
          }
        }
      })
      .catch((error) => {
        // On parse error remove uploaded photo and reset receipt-related state
        console.error("Receipt parse failed", error)
        changeHandler({ target: { name: "photos", value: [] } })
        setReceiptImageUrl([])
        setisUploadReceipt(false)
      })
  }, [receipts?.length]) //eslint-disable-line

  useEffect(() => {
    if (typeof onChanged === "function") onChanged(isChanged)
  }, [isChanged, onChanged])

  // Rounded numeric helpers for disable logic
  const itemTotalRounded = Number(totalExpenseItemAmount.toFixed(2))
  // Use Number.isNaN instead of global isNaN per lint recommendation; pre-parse once for clarity
  const parsedAmount = Number.parseFloat(form.amount)
  const formAmountRounded = Number(Number.isNaN(parsedAmount) ? 0 : Number.parseFloat(parsedAmount.toFixed(2)))
  const hasReceiptContext = expense?.receipts?.length > 0 && expense?.receipts?.[0]?.url != null
  const disableOverItemTotal = hasReceiptContext && itemTotalRounded > formAmountRounded

  const handleAddCategory = async () => {
    const result = await dispatch(
      addCategory({
        expense_category: {
          name: categoryName
        }
      })
    )
    // If API call is successful, close modal and fetch latest metadata from backend
    if (!result?.error) {
      toaster.success(result?.message)
      setCategoryName("")
      setIsOpenAddCategoryModal(false)
    }
  }
  return (
    <FirstLoading name="accounting.expense" new={!expense?.loading}>
      <form onSubmit={(e) => submitHandler(e, isExpenseItemsChanged)}>
        <div>
          <Flex gap={20} className="bg-white rounded p-l">
            <h1 className="h3 fw-medium lh-1">{t(`expense.${isExpensePersisted ? "edit" : "add"}`)}</h1>
          </Flex>
          <Flex vertical gap={20} className="bg-white rounded p-xl">
            <Flex vertical gap={6}>
              <Text fw="500">{t("expense.upload_receipt")}</Text>
              <Loading loading={receiptParsing} className="me-auto">
                <PhotoSelector imageHeight={90} form={form} changeHandler={changeHandler} small start Text={false} max={1} />
              </Loading>
              {receipts?.length > 0 && (
                <div className="mt-2">
                  <Button className={styles.removeReceiptButton} color="primary" variant="link" onClick={handleRemoveReceipt}>
                    {t("expense.remove_picture", { defaultValue: "Remove Picture" })}
                  </Button>
                </div>
              )}
            </Flex>
            <Row gutter={[20, 20]}>
              <Col span={12}>
                <Flex vertical gap={6}>
                  <Text fw="500">{t("expense.merchant_name")}*</Text>
                  <Input
                    id="merchant_name"
                    type="text"
                    name="merchant_name"
                    placeholder="Merchant Name"
                    value={form.merchant_name || ""}
                    onChange={changeHandler}
                    className="fs-6"
                  />
                </Flex>
              </Col>
              <Col span={12}>
                <Flex vertical gap={6}>
                  <Text fw="500">{t("expense.date_of_purchase")}*</Text>
                  <DatePicker
                    name="purchase_date"
                    value={form?.purchase_date ? dayjs(form.purchase_date) : dayjs()}
                    className="w-full p-xs"
                    placeholder="MM/DD/YYYY"
                    format="MM/DD/YYYY"
                    disabledDate={(current) => current && current > Date.now()}
                    onChange={(_, dateString) => {
                      changeHandler({ target: { name: "purchase_date", value: dateString } })
                    }}
                  />
                </Flex>
              </Col>
              <Col span={6}>
                <Flex gap={10} align="center" className="h-full">
                  <Input
                    type="checkbox"
                    name="recurring"
                    value="true"
                    checked={[true, "true"].includes(form.recurring)}
                    onChange={changeHandler}
                    className="mt-0"
                  />
                  <span className={`text-dark ${[true, "true"].includes(form.recurring) ? "" : "text-opacity-50"}`}>
                    {"Recurring Receipt"}
                  </span>
                </Flex>
              </Col>
              {/* {form.recurring && ( */}
              <Col span={5}>
                <Select
                  value={form.recurring ? form.recurring_type : undefined}
                  onChange={(value) => changeHandler({ target: { name: "recurring_type", value } })}
                  style={{ minWidth: 120 }}
                  options={[
                    { value: "monthly", label: t("expense.monthly") },
                    { value: "yearly", label: t("expense.yearly") }
                  ]}
                  suffixIcon={<Icon iconName="SidebarToggle" size={16} className={styles.recurringDropdown} />}
                  popupClassName="recurring-type-dropdown"
                  size="large"
                />
              </Col>
              {/* )} */}
            </Row>

            <div>
              {/* Dynamic expense items rows */}
              <Row align="start" gutter={[20, 10]} className="mb-xs">
                <Col span={filteredExpenseItems.length > 1 ? 6 : 7}>
                  <Text fw="500" className="block">
                    {t("expense.item_name")}*
                  </Text>
                </Col>
                <Col span={filteredExpenseItems.length > 1 ? 6 : 7}>
                  <Text fw="500" className="block">
                    {t("expense.amount")}*
                  </Text>
                </Col>
                <Col span={10}>
                  <Text fw="500" className="block">
                    {t("expense.category")}*
                  </Text>
                </Col>
              </Row>
              {expenseItems.map(
                (item, idx) =>
                  !item._destroy && (
                    <Row align="center" gutter={[20, 10]} key={item.id ? `item-${item.id}` : `item-${idx}`} className="mb-m">
                      <Col span={filteredExpenseItems.length > 1 ? 6 : 7}>
                        <Input
                          type="text"
                          name="name"
                          value={item.name}
                          onChange={(e) => handleExpenseItemChange(idx, "name", e.target.value)}
                          withError
                        />
                      </Col>
                      <Col span={filteredExpenseItems.length > 1 ? 6 : 7}>
                        <div className={styles.amountInputWrapper}>
                          <Text className={styles.amountDollar}>$</Text>
                          <Input
                            type="number"
                            name="amount"
                            value={item.amount}
                            onChange={(e) => handleExpenseItemChange(idx, "amount", e.target.value)}
                            min={0}
                            step="any"
                            withError
                            className={`fs-6 ${styles.amountInput}`}
                          />
                        </div>
                      </Col>
                      <Col span={10}>
                        <Select
                          className={`w-full ${styles.categorySelect}`}
                          value={item?.expense_category_id === "add-new-type" ? undefined : item?.expense_category_id || undefined}
                          onChange={(value) => {
                            if (value === "add-new-type") {
                              setIsOpenAddCategoryModal(true)
                              // Do not update the selected value
                              return
                            }
                            handleExpenseItemChange(idx, "expense_category_id", value)
                          }}
                          style={{ minWidth: 140 }}
                          suffixIcon={<Icon iconName="SidebarToggle" size={16} className="text-dark text-opacity-50" />}
                          placeholder="Choose Category"
                          options={[
                            ...metadata.expense_categories.map((category) => ({
                              value: category.id,
                              label: category.name
                            })),
                            {
                              value: "add-new-type",
                              label: (
                                <Text
                                  fw="500"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    setIsOpenAddCategoryModal(true)
                                  }}
                                >
                                  {t("expense.add_new_type")}
                                </Text>
                              )
                            }
                          ]}
                        />
                        <InputError field="category" className="mt-1 mb-n3" />
                      </Col>
                      {/* Delete icon button: show only if more than one expense item */}
                      {expenseItems.filter((i) => !i._destroy).length > 1 && (
                        <Col span={2}>
                          <Flex align="end" className="h-full pb-xs" onClick={() => handleRemoveExpenseItem(idx)}>
                            <IconWithBg className={styles.deleteIcon} height={18} width={18} icon={<Delete fill="#ff0000" />} />
                          </Flex>
                        </Col>
                      )}
                    </Row>
                  )
              )}
            </div>
            <div className="flex justify-start items-end" style={{ marginTop: "-15px" }}>
              <Button className="p-0" type="link" color="primary" variant="link" onClick={handleAddExpenseItem}>
                <PlusIcon className="me-1" />
                {t("expense.add_more")}
              </Button>
            </div>

            <div>
              <Text fw="500" className="pb-xxs block">
                {t("expense.notes")}
              </Text>
              <Input
                id="notes"
                type="textarea"
                name="notes"
                rows={10}
                placeholder={t("expense.add_notes")}
                value={form.notes || ""}
                onChange={changeHandler}
                className="fs-6"
                withError
              />
            </div>
            {isShowOverrideAmountCheckbox && (
              <Row gutter={[20, 20]}>
                <Col span={24}>
                  <Flex gap={10} align="center" className="h-full">
                    <Input
                      type="checkbox"
                      name="override_amount"
                      value="true"
                      checked={isChecked}
                      onChange={(e) => {
                        setIsChecked(e.target.checked)
                        changeHandler(e)
                      }}
                      className="mt-0"
                    />
                    <span className={`text-dark`}>{"Override amount"}</span>
                  </Flex>
                </Col>
              </Row>
            )}
          </Flex>

          {expenseAmountError && !isChecked && (
            <Tag color="error" className={`p-xs ${styles.warningContainer}`}>
              {expenseAmountError}
            </Tag>
          )}
          {!expenseAmountError && expenseAmountInfo && !isChecked && (
            <Tag color="orange" className={`p-xs ${styles.warningContainer}`}>
              {expenseAmountInfo}
            </Tag>
          )}
          <Flex justify="end" className="bg-white rounded p-xl">
            <Button
              htmlType="submit"
              color="primary"
              variant="solid"
              // Enable when override checked; require required fields still changed or items changed
              disabled={
                isSaving ||
                receiptParsing ||
                expense?.loading ||
                (!isChecked && !isChanged && !isExpenseItemsChanged) ||
                (!isChecked && disableOverItemTotal)
              }
            >
              {t("global.save")}
            </Button>
            {/* </div> */}
          </Flex>
        </div>
      </form>
      {isOpenAddCategoryModal && (
        <AddCategoryModal
          visible={isOpenAddCategoryModal}
          onCancel={() => {
            setIsOpenAddCategoryModal(false)
            setCategoryName("")
          }}
          setCategoryName={setCategoryName}
          categoryName={categoryName}
          onAdd={handleAddCategory}
        />
      )}
    </FirstLoading>
  )
}

Form.propTypes = {
  onChanged: PropTypes.func,
  expenseId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  expense: PropTypes.object,
  setVisible: PropTypes.func
}

AddCategoryModal.propTypes = {
  visible: PropTypes.bool,
  onCancel: PropTypes.func,
  setCategoryName: PropTypes.func,
  categoryName: PropTypes.string,
  onAdd: PropTypes.func,
  isSaving: PropTypes.bool
}

export default Form
